import { useMemo, useRef, useState } from 'react'
// eslint-disable-next-line no-restricted-imports -- DS2026 Phosphor icon set
import {
  PiArrowCounterClockwise,
  PiBuildings,
  PiCaretDown,
  PiCaretLeft,
  PiCaretRight,
  PiCheckCircle,
  PiEnvelopeSimple,
  PiEye,
  PiMagnifyingGlass,
  PiPaperPlaneTilt,
  PiPencilSimple,
  PiUser,
  PiWarning,
} from 'react-icons/pi'
import DOMPurify from 'dompurify'
import { toast } from 'react-toastify'

import FbButton from 'components/FbUI/FbButton'
import { StatusBadge } from 'components/FbUI/StatusBadge'
import { cn } from 'components/UI/cn'
import { useEditorCore, EditorToolbar } from 'components/Editor/EditorCore'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import 'components/Editor/Editor.css'
import {
  useGetCampaignLeadReview,
  type CampaignLeadReviewItem,
} from '../campaigns_api'
import { useGetCampaignSequence, type SequenceStep } from '../sequences_api'
import {
  useGetEmailOverrides,
  useSaveEmailOverrides,
  useDeleteEmailOverride,
  useApplyEmailOverrides,
} from '../email_overrides_api'
import {
  detectUnfilledVariables,
  fillVariables,
  getMissingValuesLabel,
  getMissingValuesMessage,
  mergeVariables,
  countContactsWithUnfilledVariables,
  buildContactTokens,
  contactDisplayName,
  VARIABLE_MENU_CONTENT_CLASS,
  VARIABLE_MENU_ITEM_CLASS,
  type EmailVariable,
} from '../email-variables'
import { useCampaignVariables } from '../columns/useCampaignVariables'
import { useCampaignAITokensByContact } from '../columns/useCampaignAITokens'
import { useGetCampaignSenderEmails } from '../sender_emails_api'
import { useGetCampaignSettings } from 'services/campaigns'
import { FormatBadge } from '../FormatBadge'
import { SignatureDisclaimer } from '../SignatureDisclaimer'
import { applyPreviewDisplayFixes } from '../previewDisplayFixes'
import { renderEmail } from '../../../lib/email-render/renderEmail'

import { WizardStepFooter, WIZARD_BUTTON_MIN_W } from './WizardStepFooter'
import { WizardStepHeader } from './WizardStepHeader'
import { SendStepTestEmailModal } from '../SendStepTestEmailModal'

interface WizardReviewEmailsStepProps {
  campaignId: number
  bisonCampaignId: number
  onBack: () => void
  onNext: () => void
  stepNumber: number
  totalSteps: number
  isLiveCampaign?: boolean
  readOnly?: boolean
}

/** Key format: `${leadId}_${stepId}` */
type OverrideKey = string

interface LocalOverride {
  subject: string
  body: string
}

export function WizardReviewEmailsStep({
  campaignId,
  bisonCampaignId,
  onBack,
  onNext,
  stepNumber,
  totalSteps,
  isLiveCampaign = false,
  readOnly = false,
}: WizardReviewEmailsStepProps) {
  const { data: leadReview, isLoading: leadsLoading } =
    useGetCampaignLeadReview(campaignId)
  const { data: sequence, isLoading: seqLoading } =
    useGetCampaignSequence(campaignId)
  const { data: savedOverrides = [], isLoading: overridesLoading } =
    useGetEmailOverrides(bisonCampaignId)
  const saveMutation = useSaveEmailOverrides(bisonCampaignId)
  const deleteMutation = useDeleteEmailOverride(bisonCampaignId)
  const applyMutation = useApplyEmailOverrides(bisonCampaignId)
  const { data: senderEmails } = useGetCampaignSenderEmails(bisonCampaignId)
  const senderSignature = senderEmails?.results?.[0]?.email_signature ?? ''
  const mailboxAddress = senderEmails?.results?.[0]?.email ?? null
  const multipleMailboxesAttached = (senderEmails?.results?.length ?? 0) > 1
  const { data: campaignSettings } = useGetCampaignSettings(
    campaignId,
    bisonCampaignId > 0
  )
  const isPlainText = campaignSettings?.plain_text ?? true

  const customVariables = useCampaignVariables(campaignId)
  const allVariables = mergeVariables(customVariables)
  const aiTokensByContact = useCampaignAITokensByContact(campaignId)

  const contacts = useMemo(
    () =>
      (leadReview?.results ?? []).filter(
        (c) => c.is_included && c.email && c.bison_lead_id != null
      ),
    [leadReview]
  )
  const steps = sequence?.steps ?? []

  const [selectedContactId, setSelectedContactId] = useState<number | null>(
    null
  )
  const [activeStepId, setActiveStepId] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [editingKey, setEditingKey] = useState<OverrideKey | null>(null)
  const [localOverrides, setLocalOverrides] = useState<
    Record<OverrideKey, LocalOverride>
  >({})
  const [savingKeys, setSavingKeys] = useState<Set<OverrideKey>>(new Set())
  const [showTestEmailModal, setShowTestEmailModal] = useState(false)

  // Initialize selections once data loads
  const resolvedContactId =
    selectedContactId ?? (contacts.length > 0 ? contacts[0].contact_id : null)
  const resolvedStepId = activeStepId ?? (steps.length > 0 ? steps[0].id : null)

  const selectedContact = contacts.find(
    (c) => c.contact_id === resolvedContactId
  )
  const currentStep = steps.find((s) => s.id === resolvedStepId)

  // Build merged override lookup: server-saved + local edits
  const overrideMap = useMemo(() => {
    const map: Record<OverrideKey, LocalOverride> = {}
    for (const o of savedOverrides) {
      map[`${o.lead_id}_${o.step_id}`] = {
        subject: o.custom_subject,
        body: o.custom_body,
      }
    }
    // Local overrides take precedence
    for (const [key, val] of Object.entries(localOverrides)) {
      map[key] = val
    }
    return map
  }, [savedOverrides, localOverrides])

  // Contacts that have overrides (for badges)
  const contactsWithOverrides = useMemo(() => {
    const ids = new Set<number>()
    for (const key of Object.keys(overrideMap)) {
      const leadId = parseInt(key.split('_')[0], 10)
      if (!isNaN(leadId)) ids.add(leadId)
    }
    return ids
  }, [overrideMap])

  // Map contact_id → bison_lead_id for override key generation
  const leadIdForContact = useMemo(() => {
    const map: Record<number, number> = {}
    for (const c of contacts) {
      if (c.bison_lead_id) {
        map[c.contact_id] = c.bison_lead_id
      }
    }
    return map
  }, [contacts])

  const resolvedLeadId = resolvedContactId
    ? leadIdForContact[resolvedContactId]
    : undefined
  const overrideKey =
    resolvedLeadId && resolvedStepId
      ? `${resolvedLeadId}_${resolvedStepId}`
      : null
  const hasOverride = overrideKey
    ? overrideMap[overrideKey] !== undefined
    : false
  const isEditing = editingKey !== null && editingKey === overrideKey

  // Count only persisted overrides, not unsaved local edits
  const totalOverrides = savedOverrides.length
  const primaryButtonBaseLabel = isLiveCampaign ? 'Done' : 'Continue to Launch'
  const primaryButtonEditsSuffix =
    totalOverrides > 0
      ? ` (${totalOverrides} edit${totalOverrides !== 1 ? 's' : ''})`
      : ''

  // Search filter
  const filteredContacts = useMemo(() => {
    if (!searchQuery) return contacts
    const q = searchQuery.toLowerCase()
    return contacts.filter(
      (c) =>
        (c.full_name || '').toLowerCase().includes(q) ||
        (c.company_name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q)
    )
  }, [contacts, searchQuery])

  const renderMode = isPlainText ? 'plaintext' : 'html'

  // Subject uses the non-escaping `fillVariables` because it's rendered as a
  // React text node which auto-escapes. The same non-escaping path also feeds
  // warning detection: `renderEmail` collapses missing `{VARNAME}` tokens to
  // empty strings, hiding them from `detectUnfilledVariables`.
  function resolveTemplates(
    step: SequenceStep,
    contact: CampaignLeadReviewItem
  ) {
    const leadId = leadIdForContact[contact.contact_id]
    const key = leadId ? `${leadId}_${step.id}` : null
    const override = key ? overrideMap[key] : undefined
    const tokens = {
      ...buildContactTokens(contact),
      ...(aiTokensByContact[contact.contact_id] ?? {}),
    }
    return {
      tokens,
      subjectTemplate: override?.subject ?? step.email_subject ?? '',
      bodyTemplate: override?.body ?? step.email_body ?? '',
    }
  }

  function getRenderedEmail(
    step: SequenceStep,
    contact: CampaignLeadReviewItem
  ) {
    const { tokens, subjectTemplate, bodyTemplate } = resolveTemplates(
      step,
      contact
    )
    return {
      subject: fillVariables(subjectTemplate, tokens),
      body: renderEmail({
        bodyHtml: bodyTemplate,
        signatureHtml: senderSignature,
        variables: tokens,
        mode: renderMode,
      }),
      bodyForWarnings: fillVariables(bodyTemplate, tokens),
    }
  }

  // Cheap warnings-only path — skips the full `renderEmail` DOMParser pass.
  // Used by the unresolvedEmailWarnings scan over every contact × step pair.
  function getRenderedEmailWarnings(
    step: SequenceStep,
    contact: CampaignLeadReviewItem
  ): string[] {
    const { tokens, subjectTemplate, bodyTemplate } = resolveTemplates(
      step,
      contact
    )
    return Array.from(
      new Set([
        ...detectUnfilledVariables(fillVariables(subjectTemplate, tokens)),
        ...detectUnfilledVariables(fillVariables(bodyTemplate, tokens)),
      ])
    )
  }

  const unresolvedEmailWarnings = useMemo(() => {
    return contacts.flatMap((contact) =>
      steps
        .map((step) => ({
          contact,
          step,
          warnings: getRenderedEmailWarnings(step, contact),
        }))
        .filter((item) => item.warnings.length > 0)
    )
    // The rendered values depend on these maps in addition to contacts/steps,
    // plus renderMode and senderSignature (used by renderEmail inside).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contacts,
    steps,
    leadIdForContact,
    overrideMap,
    aiTokensByContact,
    renderMode,
    senderSignature,
  ])

  // Banner copy counts the distinct contacts with an unfilled variable, not the
  // per-(contact, step) entries above: otherwise one contact missing a first
  // name in a 4-step sequence reads as "4 emails".
  const unresolvedContactCount = countContactsWithUnfilledVariables(
    unresolvedEmailWarnings
  )

  // Contacts whose email still has an unfilled variable — used to highlight and
  // float those rows to the top of the list so customers don't have to hunt
  // through every contact to find what needs fixing (FIR-7527).
  const contactIdsWithWarnings = useMemo(
    () =>
      new Set(unresolvedEmailWarnings.map((item) => item.contact.contact_id)),
    [unresolvedEmailWarnings]
  )

  const orderedContacts = useMemo(() => {
    // Stable sort: rows with unfilled variables first, original order preserved
    // within each group.
    return [...filteredContacts].sort(
      (a, b) =>
        Number(contactIdsWithWarnings.has(b.contact_id)) -
        Number(contactIdsWithWarnings.has(a.contact_id))
    )
  }, [filteredContacts, contactIdsWithWarnings])

  // Render the selected contact's email once per state change and reuse it
  // for the preview panes + the per-contact test send payload. The deps below
  // cover everything getRenderedEmail closes over (template, override, tokens,
  // renderMode, senderSignature).
  const selectedRendered = useMemo(
    () =>
      currentStep && selectedContact
        ? getRenderedEmail(currentStep, selectedContact)
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentStep,
      selectedContact,
      overrideMap,
      aiTokensByContact,
      leadIdForContact,
      renderMode,
      senderSignature,
    ]
  )

  const selectedEmailWarnings = useMemo(
    () =>
      selectedRendered
        ? Array.from(
            new Set([
              ...detectUnfilledVariables(selectedRendered.subject),
              // Use bodyForWarnings (raw fillVariables pass) — renderEmail
              // collapses missing tokens to empty, which would hide warnings.
              ...detectUnfilledVariables(selectedRendered.bodyForWarnings),
            ])
          )
        : [],
    [selectedRendered]
  )
  const hasSelectedEmailWarnings = selectedEmailWarnings.length > 0
  const canSaveCurrentOverride =
    !hasSelectedEmailWarnings && !savingKeys.has(overrideKey!)

  const renderedForSelectedContact =
    selectedRendered && selectedContact
      ? {
          subject: selectedRendered.subject,
          // renderEmail already converted HTML→plaintext and appended the
          // signature in one pass — body is already in the correct format.
          body: selectedRendered.body,
          plainText: isPlainText,
          contactName: selectedContact.full_name || '',
        }
      : null

  function startEditing() {
    if (!currentStep || !selectedContact || !overrideKey) return
    if (!overrideMap[overrideKey]) {
      // Pre-fill with the template (with variable tokens preserved for editing)
      setLocalOverrides((prev) => ({
        ...prev,
        [overrideKey]: {
          subject: currentStep.email_subject,
          body: currentStep.email_body,
        },
      }))
    }
    setEditingKey(overrideKey)
  }

  async function saveEdit() {
    if (!overrideKey || !resolvedStepId) return

    const override = overrideMap[overrideKey]
    if (!override) {
      setEditingKey(null)
      return
    }

    if (!resolvedLeadId) return
    const leadId = resolvedLeadId

    setSavingKeys((prev) => new Set(prev).add(overrideKey))
    try {
      await saveMutation.mutateAsync({
        overrides: [
          {
            lead_id: leadId,
            step_id: resolvedStepId,
            custom_subject: override.subject,
            custom_body: override.body,
          },
        ],
      })
      // Clear local override since it's now persisted
      setLocalOverrides((prev) => {
        const next = { ...prev }
        delete next[overrideKey]
        return next
      })
      setEditingKey(null)
      // For live campaigns, apply overrides to Bison immediately
      if (isLiveCampaign) {
        try {
          await applyMutation.mutateAsync()
          toast.success('Override applied to live campaign')
        } catch {
          toast.error('Override saved but failed to apply to live campaign')
        }
      }
    } catch {
      toast.error('Failed to save override')
    } finally {
      setSavingKeys((prev) => {
        const next = new Set(prev)
        next.delete(overrideKey)
        return next
      })
    }
  }

  async function resetOverride() {
    if (!overrideKey) return

    // Find persisted override to delete
    const persisted = savedOverrides.find(
      (o) => `${o.lead_id}_${o.step_id}` === overrideKey
    )

    // Clear local
    setLocalOverrides((prev) => {
      const next = { ...prev }
      delete next[overrideKey]
      return next
    })
    setEditingKey(null)

    // Delete from server if persisted
    if (persisted) {
      try {
        await deleteMutation.mutateAsync(persisted.id)
        if (isLiveCampaign) {
          await applyMutation.mutateAsync()
        }
      } catch {
        toast.error('Failed to reset override')
      }
    }
  }

  function updateOverride(field: 'subject' | 'body', value: string) {
    if (!overrideKey) return
    setLocalOverrides((prev) => ({
      ...prev,
      [overrideKey]: {
        ...(prev[overrideKey] ??
          overrideMap[overrideKey] ?? { subject: '', body: '' }),
        [field]: value,
      },
    }))
  }

  const isLoading = leadsLoading || seqLoading || overridesLoading

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent" />
          <p className="text-sm text-fg-secondary">Loading email review...</p>
        </div>
      </div>
    )
  }

  if (contacts.length === 0 || steps.length === 0) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-fg-secondary">
              {contacts.length === 0
                ? 'No contacts synced to this campaign yet.'
                : 'No sequence steps found. Build your sequence first.'}
            </p>
          </div>
        </div>
        <div className="border-t border-line bg-surface px-6 py-4">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 rounded-md border border-line px-4 py-2 text-sm hover:bg-surface-hover"
          >
            <PiCaretLeft className="size-4" />
            Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <WizardStepHeader
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        title="Review Emails"
        badge={<StatusBadge color="neutral">Optional</StatusBadge>}
        description={
          readOnly
            ? 'Preview the emails each contact will receive.'
            : 'Preview the exact email each contact will receive. Edit any individual email to personalize further.'
        }
      />

      {/* Active campaign banner */}
      {readOnly && (
        <div className="border-b border-alert-warning-border bg-alert-warning-bg px-6 py-3">
          <p className="mx-auto max-w-[1200px] text-sm text-alert-warning-title">
            This campaign is active. Pause the campaign to edit email overrides.
          </p>
        </div>
      )}

      {/* Split panel */}
      <div className="flex flex-1 overflow-hidden rounded-bl-2xl border-b border-l border-line bg-surface-bright">
        {/* Left: Contact list */}
        <div className="flex w-[320px] shrink-0 flex-col rounded-tr-xl border-r border-t border-line bg-surface-page">
          <div className="border-b border-line p-3">
            <div className="relative flex h-9 items-center rounded-md border border-neutral-300 bg-white shadow-[0px_1px_2px_0px_rgba(16,24,40,0.06),0px_1px_1px_0px_rgba(16,24,40,0.04)] focus-within:border-neutral-400">
              <PiMagnifyingGlass className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-brand-bright" />
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-full w-full rounded-md bg-transparent pl-9 pr-3 text-sm tracking-[-0.2px] text-fg outline-none placeholder:text-fg-secondary"
              />
            </div>
            <p className="mt-2 px-1 text-xs text-fg-secondary">
              {filteredContacts.length} contact
              {filteredContacts.length !== 1 ? 's' : ''} in sequence
            </p>
          </div>
          <div className="flex-1 overflow-auto bg-surface-page">
            {orderedContacts.map((contact) => {
              const isSelected = resolvedContactId === contact.contact_id
              const leadId =
                leadIdForContact[contact.contact_id] ?? contact.contact_id
              const hasCustom = contactsWithOverrides.has(leadId)
              const hasWarning = contactIdsWithWarnings.has(contact.contact_id)

              return (
                <button
                  type="button"
                  key={contact.contact_id}
                  onClick={() => {
                    setSelectedContactId(contact.contact_id)
                    setEditingKey(null)
                  }}
                  className={cn(
                    'w-full border-b border-l-2 border-l-transparent border-line px-4 py-3 text-left transition-colors',
                    isSelected && hasWarning
                      ? 'bg-warning-bg-subtle ring-1 ring-inset ring-warning-strong'
                      : isSelected
                        ? 'bg-neutral-100'
                        : hasWarning
                          ? 'bg-warning-bg-subtle hover:bg-warning-bg-subtle/70'
                          : 'hover:bg-neutral-50',
                    hasWarning && 'border-l-warning-strong'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        'flex size-9 shrink-0 items-center justify-center rounded-full',
                        isSelected ? 'bg-neutral-200' : 'bg-neutral-100'
                      )}
                    >
                      <PiUser className="size-4 text-fg-secondary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm">
                          {contact.full_name}
                        </span>
                        {hasWarning && (
                          <StatusBadge color="warning" icon={PiWarning}>
                            Needs fix
                          </StatusBadge>
                        )}
                        {hasCustom && (
                          <StatusBadge color="info">Edited</StatusBadge>
                        )}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5 text-xs text-fg-secondary">
                        <PiBuildings className="size-3 shrink-0" />
                        <span className="truncate">{contact.company_name}</span>
                      </div>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Right: Email preview/editor */}
        <div className="flex-1 overflow-auto bg-surface-bright">
          {selectedContact && currentStep && (
            <div className="mx-auto max-w-3xl p-6">
              {/* Contact header */}
              <div className="mb-4 rounded-xl border border-line bg-surface p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="flex size-11 items-center justify-center rounded-full bg-surface-alt">
                    <PiUser className="size-5 text-fg-secondary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-base font-medium text-fg">
                      {selectedContact.full_name}
                    </p>
                    <p className="text-sm text-fg-secondary">
                      {selectedContact.title}
                      {selectedContact.title && selectedContact.company_name
                        ? ' at '
                        : ''}
                      {selectedContact.company_name}
                    </p>
                    <p className="mt-0.5 flex items-center gap-1 text-sm text-fg-secondary">
                      <PiEnvelopeSimple className="size-3.5" />
                      {selectedContact.email}
                    </p>
                  </div>
                  {overrideKey &&
                    contactsWithOverrides.has(
                      parseInt(overrideKey.split('_')[0], 10)
                    ) && <StatusBadge color="info">Edited</StatusBadge>}
                </div>
              </div>

              {/* Step tabs */}
              {steps.length > 1 && (
                <div className="mb-4 flex gap-1.5">
                  {steps.map((step, index) => {
                    const stepKey = resolvedLeadId
                      ? `${resolvedLeadId}_${step.id}`
                      : null
                    const stepHasOverride = stepKey
                      ? overrideMap[stepKey] !== undefined
                      : false

                    return (
                      <button
                        type="button"
                        key={step.id}
                        onClick={() => {
                          setActiveStepId(step.id)
                          setEditingKey(null)
                        }}
                        className={cn(
                          'flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs transition-colors',
                          resolvedStepId === step.id
                            ? 'bg-brand text-white'
                            : 'border border-line bg-surface text-fg-secondary hover:bg-surface-hover'
                        )}
                      >
                        <span
                          className={cn(
                            'flex size-5 items-center justify-center rounded-full text-xxs',
                            resolvedStepId === step.id
                              ? 'bg-white/20'
                              : 'bg-surface-hover'
                          )}
                        >
                          {index + 1}
                        </span>
                        Step {index + 1}
                        {stepHasOverride && (
                          <PiPencilSimple className="size-2.5" />
                        )}
                      </button>
                    )
                  })}
                </div>
              )}

              {/* Email preview / editor card */}
              <div className="overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
                {/* Toolbar */}
                <div className="flex items-center justify-between border-b border-line bg-surface-page px-4 py-3">
                  <div className="flex items-center gap-2 text-sm text-fg-secondary">
                    <PiEye className="size-3.5" />
                    {isEditing ? 'Editing' : 'Preview'} for Step{' '}
                    {steps.indexOf(currentStep) + 1}
                    {hasOverride && !isEditing && (
                      <StatusBadge color="info">Edited</StatusBadge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={resetOverride}
                          className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-brand-bright hover:text-brand-active"
                        >
                          <PiArrowCounterClockwise className="size-3" />
                          Reset to default
                        </button>
                        <FbButton
                          type="button"
                          variant="primary"
                          size="sm"
                          onClick={saveEdit}
                          disabled={!canSaveCurrentOverride}
                          loading={savingKeys.has(overrideKey!)}
                        >
                          <PiCheckCircle className="size-3.5" />
                          Done Editing
                        </FbButton>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setShowTestEmailModal(true)}
                          disabled={!currentStep?.id}
                          title={
                            !currentStep?.id
                              ? 'Save the sequence first to send a test'
                              : `Send a test of Step ${steps.indexOf(currentStep) + 1}`
                          }
                          className="flex items-center gap-1.5 rounded-md border border-line bg-white px-3 py-1.5 text-sm hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <PiPaperPlaneTilt className="size-3.5" />
                          Send test email
                        </button>
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={startEditing}
                            className="flex items-center gap-1.5 rounded-md border border-line bg-white px-3 py-1.5 text-sm hover:bg-surface-hover"
                          >
                            <PiPencilSimple className="size-3.5" />
                            Edit for{' '}
                            {(selectedContact.full_name || '').split(' ')[0]}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Email content */}
                <div className="space-y-4 p-5">
                  {/* Subject */}
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-fg-secondary">
                      Subject
                    </label>
                    {isEditing ? (
                      <div>
                        <input
                          type="text"
                          value={overrideMap[overrideKey!]?.subject ?? ''}
                          onChange={(e) =>
                            updateOverride('subject', e.target.value)
                          }
                          className="w-full rounded-lg border border-line px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand/20"
                        />
                        <VariableInsertBar
                          onInsert={(variable) => {
                            const current =
                              overrideMap[overrideKey!]?.subject ?? ''
                            if (variable.hasMissingValues) {
                              toast.warning(getMissingValuesMessage(variable))
                            }
                            updateOverride('subject', current + variable.value)
                          }}
                          variables={allVariables}
                        />
                      </div>
                    ) : (
                      <div className="rounded-lg bg-surface-alt px-3 py-2 text-sm">
                        {selectedRendered?.subject || (
                          <span className="italic text-fg-secondary">
                            No subject
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {hasSelectedEmailWarnings && (
                    <div className="flex items-start gap-2 rounded-md border !border-alert-warning-border bg-alert-warning-bg px-3 py-2 text-xs text-alert-warning-title">
                      <PiWarning className="mt-0.5 size-3.5 shrink-0" />
                      <div>
                        <p>
                          Some variables could not be filled for this contact.
                          Fix or remove them before continuing.
                        </p>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {selectedEmailWarnings.map((warning) => (
                            <code
                              key={warning}
                              className="rounded bg-warning-bg px-1.5 py-0.5 text-xxs"
                            >
                              {warning}
                            </code>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Body */}
                  <div>
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      <label className="block text-sm font-medium text-fg-secondary">
                        Body
                      </label>
                      <FormatBadge plainText={isPlainText} />
                    </div>
                    {isEditing ? (
                      <OverrideBodyEditor
                        value={overrideMap[overrideKey!]?.body ?? ''}
                        onChange={(val) => updateOverride('body', val)}
                        variables={allVariables}
                        isPlainText={isPlainText}
                      />
                    ) : (
                      <div className="min-h-[200px] rounded-lg bg-surface-alt px-4 py-3 text-sm leading-relaxed">
                        {isPlainText ? (
                          // renderEmail already converted HTML→plaintext and
                          // appended the signature — render as-is, with display-
                          // only fixes for hyphens and dashes so they don't
                          // trigger line-breaks (see previewDisplayFixes).
                          <pre className="whitespace-pre-wrap font-sans text-sm text-fg [overflow-wrap:normal] [word-break:normal] [hyphens:none]">
                            {applyPreviewDisplayFixes(
                              selectedRendered?.body ?? ''
                            )}
                          </pre>
                        ) : (
                          // renderEmail appended the signature HTML — sanitize
                          // the full combined output before rendering.
                          <div
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(
                                selectedRendered?.body ?? ''
                              ),
                            }}
                          />
                        )}
                      </div>
                    )}
                    {!isEditing && (
                      <SignatureDisclaimer
                        mailboxAddress={mailboxAddress}
                        multipleMailboxesAttached={multipleMailboxesAttached}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {unresolvedEmailWarnings.length > 0 && (
        <div className="border-t border-alert-warning-border bg-alert-warning-bg px-8 py-2 text-xs text-alert-warning-title">
          <div className="flex items-start gap-2">
            <PiWarning className="mt-0.5 size-3.5 shrink-0" />
            <div>
              <p>
                {unresolvedContactCount} contact
                {unresolvedContactCount === 1 ? ' has' : 's have'} variables
                that could not be filled. Review those emails before launch.
              </p>
              <p className="mt-1">
                First issue:{' '}
                {contactDisplayName(unresolvedEmailWarnings[0].contact)}, step{' '}
                {steps.indexOf(unresolvedEmailWarnings[0].step) + 1}:{' '}
                {unresolvedEmailWarnings[0].warnings.join(', ')}
              </p>
            </div>
          </div>
        </div>
      )}

      <SendStepTestEmailModal
        show={showTestEmailModal}
        onHide={() => setShowTestEmailModal(false)}
        bisonStepId={currentStep?.id ?? null}
        bisonCampaignId={bisonCampaignId}
        stepNumber={currentStep ? steps.indexOf(currentStep) + 1 : 0}
        unresolvedVariables={selectedEmailWarnings}
        renderedFor={renderedForSelectedContact}
      />

      <WizardStepFooter>
        <FbButton
          type="button"
          variant="outline"
          onClick={onBack}
          className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
        >
          <PiCaretLeft className="size-4" />
          Back
        </FbButton>
        <FbButton
          type="button"
          onClick={onNext}
          disabled={unresolvedEmailWarnings.length > 0}
          className={`${WIZARD_BUTTON_MIN_W} justify-center gap-2`}
        >
          <span>
            {primaryButtonBaseLabel}
            {primaryButtonEditsSuffix && (
              <span className="font-light opacity-80">
                {primaryButtonEditsSuffix}
              </span>
            )}
          </span>
          <PiCaretRight className="size-4" />
        </FbButton>
      </WizardStepFooter>
    </div>
  )
}

/** Rich text editor for override body — uses the same Quill editor as the sequence builder. */
function OverrideBodyEditor({
  value,
  onChange,
  variables,
  isPlainText,
}: {
  value: string
  onChange: (value: string) => void
  variables: ReadonlyArray<EmailVariable>
  isPlainText: boolean
}) {
  const pendingFocusPosition = useRef<number | null>(null)

  const { uniqueId, quillEditor, cursorPositionRef } = useEditorCore({
    value,
    onChange: (val) => onChange(val ?? ''),
    placeholder: 'Write your custom email content...',
    plainText: isPlainText,
  })

  const insertVariable = (variable: EmailVariable) => {
    if (!quillEditor) return
    if (variable.hasMissingValues) {
      toast.warning(getMissingValuesMessage(variable))
    }
    const maxPosition = Math.max(0, quillEditor.getLength() - 1)
    const position = Math.min(cursorPositionRef.current, maxPosition)
    quillEditor.insertText(position, variable.value)
    const newPosition = position + variable.value.length
    cursorPositionRef.current = newPosition
    pendingFocusPosition.current = newPosition
  }

  const handleDropdownOpenChange = (open: boolean) => {
    if (!open && pendingFocusPosition.current !== null && quillEditor) {
      const pos = pendingFocusPosition.current
      pendingFocusPosition.current = null
      requestAnimationFrame(() => {
        quillEditor.focus()
        quillEditor.setSelection(pos, 0)
      })
    }
  }

  const variableDropdownButton = (
    <DropdownMenu onOpenChange={handleDropdownOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Insert Variable"
          className="text-brand-bright hover:text-brand-active"
          style={{
            display: 'inline-flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '4px',
            height: '24px',
            padding: '0 8px',
            border: 'none',
            background: 'white',
            fontSize: '12px',
            fontWeight: 500,
            cursor: 'pointer',
            borderRadius: '3px',
          }}
        >
          <span>Variables</span>
          <PiCaretDown style={{ width: 12, height: 12, flexShrink: 0 }} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={VARIABLE_MENU_CONTENT_CLASS}
      >
        {variables.map((variable) => (
          <DropdownMenuItem
            key={variable.value}
            className={VARIABLE_MENU_ITEM_CLASS}
            onClick={() => insertVariable(variable)}
          >
            <span className="flex items-center gap-1.5">
              {variable.label}
              {variable.isCampaignColumn && (
                <span className="rounded bg-surface-hover px-1 text-xxs uppercase text-fg-secondary">
                  {variable.isManual ? 'Manual' : 'AI'}
                </span>
              )}
              {variable.hasMissingValues && (
                <PiWarning className="size-3 shrink-0 text-warning" />
              )}
            </span>
            {variable.hasMissingValues && (
              <span className="ml-2 text-xxs text-warning-strong">
                {getMissingValuesLabel(variable)}
              </span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <div
      className={cn(
        'rounded-lg bg-surface',
        isPlainText && '[&_.ql-toolbar]:!flex [&_.ql-toolbar]:!items-center'
      )}
    >
      <div id={`editor-${uniqueId}`} className="min-h-[200px] bg-surface" />
      <EditorToolbar
        uniqueId={uniqueId}
        plainText={isPlainText}
        additionalToolbarButtons={variableDropdownButton}
      />
    </div>
  )
}

/** Small bar showing clickable variable tokens for insertion (used for subject field). */
function VariableInsertBar({
  onInsert,
  variables,
}: {
  onInsert: (variable: EmailVariable) => void
  variables: ReadonlyArray<EmailVariable>
}) {
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      <span className="text-sm font-normal text-fg-secondary">Insert:</span>
      {variables.map((v) => (
        <button
          type="button"
          key={v.token}
          onClick={() => onInsert(v)}
          title={
            v.hasMissingValues
              ? getMissingValuesMessage(v)
              : `Insert ${v.label}`
          }
          className={cn(
            'inline-flex items-center gap-1 rounded-full bg-basil-50 px-2 py-0.5 text-xs font-medium text-brand-active transition-colors hover:bg-basil-100',
            v.hasMissingValues &&
              'bg-warning-bg-subtle text-warning-strong hover:bg-warning-bg-subtle'
          )}
        >
          {v.label}
          {v.hasMissingValues && (
            <PiWarning className="size-3 shrink-0 text-warning" />
          )}
        </button>
      ))}
    </div>
  )
}
